// Capexara AI — Resource Artwork
// One bespoke animated SVG illustration per article (18 total).
// Uses brand palette (ember #F26B1F, steel #1E4E8C, steel-200 #8FB3DE).
// Each illustration shares a 360x220 viewBox + dark gradient backdrop.
// Animations are CSS-keyframes from App.css (pulseDot, glowPulse, fadeInSoft).
import React from "react";

/* ============== Shared building blocks ============== */
const Bg = ({ id, dark = true }) => (
  <>
    <defs>
      <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={dark ? "#0B1220" : "#F2F4F7"} />
        <stop offset="100%" stopColor={dark ? "#070A12" : "#E8ECF1"} />
      </linearGradient>
      <pattern id={`${id}-grid`} width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M20 0H0V20" fill="none" stroke="#1E4E8C" strokeOpacity={dark ? "0.10" : "0.06"} strokeWidth="0.5" />
      </pattern>
      <radialGradient id={`${id}-emberglow`} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#F26B1F" stopOpacity="0.45" />
        <stop offset="60%" stopColor="#F26B1F" stopOpacity="0.10" />
        <stop offset="100%" stopColor="#F26B1F" stopOpacity="0" />
      </radialGradient>
      <radialGradient id={`${id}-steelglow`} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#5790C9" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#5790C9" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect x="0" y="0" width="360" height="220" fill={`url(#${id}-bg)`} />
    <rect x="0" y="0" width="360" height="220" fill={`url(#${id}-grid)`} />
  </>
);

const Frame = ({ id, children }) => (
  <svg viewBox="0 0 360 220" preserveAspectRatio="xMidYMid slice" style={{ width: "100%", height: "100%", display: "block" }}>
    <Bg id={id} />
    {children}
  </svg>
);

const styleAnimDelay = (s) => ({ animationDelay: `${s}s` });

/* ============== WHITE PAPERS ============== */

// wp-01 — The ₹200 Lakh Crore Opportunity (rising bar chart)
const WP01 = () => (
  <Frame id="wp01">
    <circle cx="180" cy="110" r="92" fill="url(#wp01-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {[
      { x: 40, h: 30 }, { x: 70, h: 52 }, { x: 100, h: 70 },
      { x: 130, h: 90 }, { x: 160, h: 108 }, { x: 190, h: 124 },
      { x: 220, h: 138 }, { x: 250, h: 156 }, { x: 280, h: 168 },
    ].map((b, i) => (
      <rect key={i} x={b.x} y={190 - b.h} width="20" height={b.h}
        fill={i >= 7 ? "#F26B1F" : "#5790C9"} rx="2"
        style={{ animation: `rise .8s cubic-bezier(.2,.8,.2,1) ${i * 0.08}s both` }} />
    ))}
    <line x1="20" y1="190" x2="340" y2="190" stroke="rgba(243,244,246,0.25)" strokeWidth="1" />
    <text x="180" y="32" textAnchor="middle" fontSize="10" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">₹200 LAKH CR · 2030</text>
    <circle cx="290" cy="34" r="2.5" fill="#F26B1F" style={{ animation: "pulseDot 1.6s ease-in-out infinite" }} />
  </Frame>
);

// wp-02 — The CPCT Blueprint (concentric control-tower rings)
const WP02 = () => (
  <Frame id="wp02">
    <circle cx="180" cy="110" r="100" fill="url(#wp02-steelglow)" style={{ animation: "glowPulse 5s ease-in-out infinite" }} />
    {[88, 70, 52, 34].map((r, i) => (
      <circle key={i} cx="180" cy="110" r={r} fill="none" stroke={i === 0 ? "#F26B1F" : "#5790C9"} strokeOpacity={0.3 + i * 0.15} strokeWidth="1" strokeDasharray={i % 2 ? "4 3" : "0"}
        style={{ transformOrigin: "180px 110px", animation: `spin ${28 - i * 4}s linear infinite ${i % 2 ? "reverse" : ""}` }} />
    ))}
    <circle cx="180" cy="110" r="14" fill="#0F1628" stroke="#F26B1F" strokeWidth="1.5" />
    <circle cx="180" cy="110" r="5" fill="#F26B1F" style={{ animation: "pulseDot 1.8s ease-in-out infinite" }} />
    {/* satellite labels */}
    {[
      { x: 268, y: 110, t: "EVM" }, { x: 92, y: 110, t: "4D" },
      { x: 180, y: 22, t: "HSE" }, { x: 180, y: 198, t: "CLAIMS" },
    ].map((s, i) => (
      <g key={i}>
        <circle cx={s.x} cy={s.y} r="3" fill="#5790C9" />
        <text x={s.x} y={s.y - 8} textAnchor="middle" fontSize="8" fill="#8FB3DE" fontFamily="var(--f-mono)" letterSpacing="1.4">{s.t}</text>
      </g>
    ))}
  </Frame>
);

// wp-03 — AWP × Generative AI (stacked work-package cards)
const WP03 = () => (
  <Frame id="wp03">
    <circle cx="120" cy="110" r="70" fill="url(#wp03-emberglow)" style={{ animation: "glowPulse 4.5s ease-in-out infinite" }} />
    {[0, 1, 2, 3].map((i) => (
      <g key={i} style={{ animation: `fadeInSoft .7s ease ${i * 0.2}s both` }}>
        <rect x={70 + i * 12} y={50 + i * 14} width="160" height="40" rx="4" fill="#0F1628" stroke={i === 3 ? "#F26B1F" : "rgba(30,78,140,0.5)"} strokeWidth="0.8" />
        <rect x={78 + i * 12} y={58 + i * 14} width="60" height="4" rx="1" fill={i === 3 ? "#F26B1F" : "#5790C9"} opacity="0.7" />
        <rect x={78 + i * 12} y={68 + i * 14} width={90 - i * 8} height="3" rx="1" fill="rgba(143,179,222,0.4)" />
        <rect x={78 + i * 12} y={76 + i * 14} width={70 - i * 6} height="3" rx="1" fill="rgba(143,179,222,0.3)" />
      </g>
    ))}
    {/* AI agent particles */}
    {[0, 1, 2, 3, 4].map((i) => (
      <circle key={i} cx={310 + Math.cos(i * 1.3) * 22} cy={120 + Math.sin(i * 1.3) * 30} r="2" fill="#F26B1F" style={{ animation: `pulseDot ${1.2 + i * 0.2}s ease-in-out infinite ${i * 0.2}s` }} />
    ))}
    <text x="180" y="200" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">AWP · GENAI · MULTI-AGENT</text>
  </Frame>
);

// wp-04 — EVM is Broken (broken graph + recovered graph)
const WP04 = () => (
  <Frame id="wp04">
    <circle cx="270" cy="100" r="80" fill="url(#wp04-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {/* broken EVM line */}
    <polyline points="30,160 60,140 80,150 110,130 140,148 170,128" fill="none" stroke="rgba(143,179,222,0.45)" strokeWidth="1.5" strokeDasharray="3 3" />
    <text x="40" y="180" fontSize="8" fill="rgba(143,179,222,0.6)" fontFamily="var(--f-mono)" letterSpacing="1.6">EVM (LEGACY)</text>
    {/* recovered cognitive line */}
    <polyline points="170,128 200,110 230,90 260,70 290,52 320,40" fill="none" stroke="#F26B1F" strokeWidth="2"
      strokeDasharray="240" strokeDashoffset="240"
      style={{ animation: "drawLine 2.4s cubic-bezier(.2,.8,.2,1) .6s forwards" }} />
    <circle cx="170" cy="128" r="4" fill="#F26B1F" />
    <circle cx="320" cy="40" r="5" fill="#F26B1F" style={{ animation: "pulseDot 1.6s ease-in-out infinite" }} />
    <text x="220" y="78" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.4" fontWeight="700">COGNITIVE FORECAST</text>
    <line x1="20" y1="190" x2="340" y2="190" stroke="rgba(243,244,246,0.18)" />
  </Frame>
);

// wp-05 — Scan-to-IFC (point cloud → mesh evolution)
const WP05 = () => (
  <Frame id="wp05">
    <circle cx="180" cy="110" r="80" fill="url(#wp05-steelglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {/* point cloud (left) */}
    <g>
      {Array.from({ length: 70 }).map((_, i) => {
        const x = 30 + (i * 17 % 90);
        const y = 50 + (i * 29 % 130);
        return <circle key={i} cx={x} cy={y} r="1" fill="#5790C9" opacity={0.3 + (i % 5) * 0.1} style={{ animation: `pulseDot ${2 + (i % 4) * 0.3}s ease-in-out infinite ${(i % 6) * 0.15}s` }} />;
      })}
    </g>
    {/* arrow */}
    <path d="M140 110 H190" stroke="#F26B1F" strokeWidth="1.5" markerEnd="url(#wp05-arrow)" />
    <defs>
      <marker id="wp05-arrow" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
        <path d="M0 0 L8 4 L0 8 Z" fill="#F26B1F" />
      </marker>
    </defs>
    {/* IFC wireframe (right) */}
    <g style={{ animation: "fadeInSoft 1s ease 0.5s both" }}>
      {[0, 1, 2].map((i) => (
        <rect key={i} x={210 + i * 8} y={60 + i * 6} width="100" height="80" fill="none" stroke="#F26B1F" strokeOpacity={0.7 - i * 0.2} strokeWidth="0.8" />
      ))}
      <line x1="210" y1="60" x2="226" y2="74" stroke="#F26B1F" strokeOpacity="0.5" />
      <line x1="310" y1="60" x2="326" y2="74" stroke="#F26B1F" strokeOpacity="0.5" />
      <line x1="210" y1="140" x2="226" y2="154" stroke="#F26B1F" strokeOpacity="0.3" />
      <line x1="310" y1="140" x2="326" y2="154" stroke="#F26B1F" strokeOpacity="0.3" />
    </g>
    <text x="60" y="200" fontSize="8" fill="rgba(143,179,222,0.7)" fontFamily="var(--f-mono)" letterSpacing="1.4">POINT CLOUD</text>
    <text x="240" y="200" fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.4" fontWeight="700">IFC · 5 DAYS</text>
  </Frame>
);

// wp-06 — Claims Management (FIDIC document with red flag highlights)
const WP06 = () => (
  <Frame id="wp06">
    <circle cx="180" cy="110" r="80" fill="url(#wp06-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    <rect x="100" y="36" width="160" height="148" rx="4" fill="#0F1628" stroke="rgba(30,78,140,0.5)" strokeWidth="0.8" />
    <text x="110" y="52" fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.6" fontWeight="700">FIDIC RED · CL 20.1</text>
    {[60, 76, 92, 108, 124, 140, 156, 172].map((y, i) => (
      <g key={i}>
        <rect x="110" y={y} width={i === 2 || i === 5 ? 90 : 140} height="3" rx="1" fill={i === 2 || i === 5 ? "#F26B1F" : "rgba(143,179,222,0.35)"} />
        {(i === 2 || i === 5) && (
          <circle cx="208" cy={y + 1.5} r="3" fill="#F26B1F" style={{ animation: `pulseDot 1.6s ease-in-out infinite ${i * 0.3}s` }} />
        )}
      </g>
    ))}
    <text x="180" y="206" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">2 CLAIMS PATTERNS DETECTED</text>
  </Frame>
);

// wp-07 — HSE Telemetry (10,000 dots → 1 dashboard funnel)
const WP07 = () => (
  <Frame id="wp07">
    <circle cx="180" cy="110" r="90" fill="url(#wp07-steelglow)" style={{ animation: "glowPulse 4.5s ease-in-out infinite" }} />
    {/* incoming dots */}
    {Array.from({ length: 60 }).map((_, i) => {
      const x = 20 + (i * 13 % 100);
      const y = 30 + (i * 19 % 160);
      return <circle key={i} cx={x} cy={y} r="1.5" fill="#5790C9" opacity={0.4 + (i % 4) * 0.15} style={{ animation: `pulseDot ${1.2 + (i % 5) * 0.2}s ease-in-out infinite ${(i % 6) * 0.1}s` }} />;
    })}
    {/* funnel */}
    <path d="M140 50 L240 50 L210 110 L210 180 L170 180 L170 110 Z" fill="rgba(30,78,140,0.18)" stroke="#5790C9" strokeWidth="0.8" />
    {/* dashboard */}
    <g style={{ animation: "fadeInSoft 1s ease .4s both" }}>
      <rect x="270" y="80" width="80" height="60" rx="4" fill="#0F1628" stroke="#F26B1F" strokeWidth="1" />
      <rect x="278" y="92" width="20" height="20" fill="rgba(242,107,31,0.4)" />
      <rect x="304" y="92" width="20" height="12" fill="#5790C9" />
      <rect x="304" y="108" width="14" height="4" fill="#5790C9" opacity="0.6" />
      <rect x="278" y="118" width="60" height="3" fill="#F26B1F" opacity="0.6" />
      <rect x="278" y="125" width="40" height="3" fill="rgba(143,179,222,0.5)" />
    </g>
    <text x="180" y="206" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">10,000 → 1 PANE</text>
  </Frame>
);

// wp-08 — Decarbonisation Capex Wall (rising green wall + smoke)
const WP08 = () => (
  <Frame id="wp08">
    <circle cx="240" cy="110" r="85" fill="url(#wp08-emberglow)" style={{ animation: "glowPulse 4.5s ease-in-out infinite" }} />
    {/* legacy emissions (left smoke trails) */}
    {[40, 60, 80].map((x, i) => (
      <path key={i} d={`M${x} 180 Q${x + 8} 140 ${x} 100 Q${x - 8} 60 ${x + 6} 30`} fill="none" stroke="rgba(143,179,222,0.4)" strokeWidth="1" strokeDasharray="2 3" style={{ animation: `pulseDot 3s ease-in-out infinite ${i * 0.4}s` }} />
    ))}
    {/* "wall" of new-build capex (vertical bars rising) */}
    {[140, 165, 190, 215, 240, 265, 290, 315].map((x, i) => {
      const h = 60 + i * 8;
      return <rect key={i} x={x} y={190 - h} width="18" height={h} rx="2" fill={i >= 6 ? "#F26B1F" : "#5790C9"} opacity={0.7 + i * 0.04} style={{ animation: `rise .8s cubic-bezier(.2,.8,.2,1) ${i * 0.07}s both` }} />;
    })}
    <line x1="20" y1="190" x2="340" y2="190" stroke="rgba(243,244,246,0.25)" strokeWidth="1" />
    <text x="180" y="32" textAnchor="middle" fontSize="10" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">₹60 LAKH CR · NET-ZERO</text>
  </Frame>
);

/* ============== BLOGS ============== */

// blog-01 — Why megaprojects overrun (7 root-cause nodes)
const BLOG01 = () => (
  <Frame id="b01">
    <circle cx="180" cy="110" r="90" fill="url(#b01-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {[0, 1, 2, 3, 4, 5, 6].map((i) => {
      const angle = (i * 360 / 7 - 90) * Math.PI / 180;
      const x = 180 + Math.cos(angle) * 70;
      const y = 110 + Math.sin(angle) * 60;
      return (
        <g key={i}>
          <line x1="180" y1="110" x2={x} y2={y} stroke="rgba(143,179,222,0.4)" strokeWidth="0.6" strokeDasharray="2 2" />
          <circle cx={x} cy={y} r={i === 0 ? 7 : 5} fill={i === 0 ? "#F26B1F" : "#5790C9"} style={{ animation: `pulseDot ${1.6 + i * 0.2}s ease-in-out infinite ${i * 0.15}s` }} />
        </g>
      );
    })}
    <circle cx="180" cy="110" r="14" fill="#0F1628" stroke="#F26B1F" strokeWidth="1.5" />
    <text x="180" y="114" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="700">7</text>
    <text x="180" y="206" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">SEVEN ROOT CAUSES</text>
  </Frame>
);

// blog-02 — FIDIC clauses GenAI shouldn't draft (warning highlights)
const BLOG02 = () => (
  <Frame id="b02">
    <circle cx="180" cy="110" r="80" fill="url(#b02-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    <rect x="80" y="40" width="200" height="140" rx="4" fill="#0F1628" stroke="rgba(30,78,140,0.5)" strokeWidth="0.8" />
    <text x="92" y="58" fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.6" fontWeight="700">CLAUSE LIBRARY · FIDIC</text>
    {[68, 84, 100, 116, 132, 148, 164].map((y, i) => {
      const danger = [1, 3, 5].includes(i);
      return (
        <g key={i}>
          <rect x="92" y={y} width="176" height="3" rx="1" fill={danger ? "#F26B1F" : "rgba(143,179,222,0.4)"} opacity={danger ? 0.9 : 0.6} />
          {danger && <text x="276" y={y + 3} fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)" textAnchor="end">⚠</text>}
          {danger && <circle cx="86" cy={y + 1.5} r="2" fill="#F26B1F" style={{ animation: `pulseDot 1.5s ease-in-out infinite ${i * 0.2}s` }} />}
        </g>
      );
    })}
  </Frame>
);

// blog-03 — P&ID scan to IFC (legacy paper → digital twin)
const BLOG03 = () => (
  <Frame id="b03">
    <circle cx="180" cy="110" r="80" fill="url(#b03-steelglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {/* paper P&ID stack */}
    {[0, 1, 2].map((i) => (
      <rect key={i} x={40 + i * 4} y={50 + i * 4} width="100" height="120" rx="2" fill="#F2F4F7" stroke="rgba(30,78,140,0.5)" strokeWidth="0.6" opacity={0.85 - i * 0.1} />
    ))}
    {/* P&ID symbols on top sheet */}
    <circle cx="60" cy="80" r="6" fill="none" stroke="#1E4E8C" strokeWidth="1" />
    <line x1="66" y1="80" x2="100" y2="80" stroke="#1E4E8C" strokeWidth="1" />
    <rect x="100" y="74" width="14" height="12" fill="none" stroke="#1E4E8C" strokeWidth="1" />
    <line x1="80" y1="100" x2="80" y2="150" stroke="#1E4E8C" strokeWidth="1" />
    <circle cx="80" cy="150" r="4" fill="none" stroke="#1E4E8C" strokeWidth="1" />
    {/* arrow */}
    <path d="M165 110 H205" stroke="#F26B1F" strokeWidth="1.5" />
    <path d="M198 105 L208 110 L198 115" fill="none" stroke="#F26B1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* digital twin */}
    <g style={{ animation: "fadeInSoft 1s ease .5s both" }}>
      <rect x="220" y="50" width="100" height="120" rx="3" fill="#0F1628" stroke="#F26B1F" strokeWidth="1" />
      <text x="228" y="64" fontSize="7" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.4" fontWeight="700">IFC.E3D</text>
      {[78, 92, 106, 120, 134, 148].map((y, i) => (
        <g key={i}>
          <circle cx={234 + (i % 3) * 30} cy={y} r="3" fill={i % 2 ? "#F26B1F" : "#5790C9"} style={{ animation: `pulseDot ${1.4 + i * 0.15}s ease-in-out infinite ${i * 0.1}s` }} />
          <line x1={234 + (i % 3) * 30} y1={y} x2={234 + ((i + 1) % 3) * 30} y2={y + 14} stroke="rgba(143,179,222,0.4)" strokeWidth="0.6" />
        </g>
      ))}
    </g>
  </Frame>
);

// blog-04 — CFOs and digital twins (₹ value flow)
const BLOG04 = () => (
  <Frame id="b04">
    <circle cx="180" cy="110" r="80" fill="url(#b04-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {[
      { x: 60, label: "MW", value: "₹/MW" },
      { x: 180, label: "KL", value: "₹/KL" },
      { x: 300, label: "MT", value: "₹/MT" },
    ].map((u, i) => (
      <g key={i}>
        <rect x={u.x - 36} y="74" width="72" height="72" rx="6" fill="#0F1628" stroke={i === 1 ? "#F26B1F" : "rgba(30,78,140,0.5)"} strokeWidth="0.8" />
        <text x={u.x} y="100" textAnchor="middle" fontSize="10" fill="rgba(143,179,222,0.55)" fontFamily="var(--f-mono)" letterSpacing="1.4">{u.value}</text>
        <text x={u.x} y="128" textAnchor="middle" fontSize="22" fill={i === 1 ? "#F26B1F" : "#8FB3DE"} fontFamily="var(--f-display)" fontWeight="600">{u.label}</text>
        <circle cx={u.x} cy="148" r="2" fill="#F26B1F" style={{ animation: `pulseDot 2s ease-in-out infinite ${i * 0.4}s` }} />
      </g>
    ))}
    <line x1="40" y1="170" x2="320" y2="170" stroke="rgba(143,179,222,0.3)" strokeDasharray="2 3" />
    <text x="180" y="194" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">VALUE PER PRODUCED UNIT</text>
  </Frame>
);

// blog-05 — Vendor scheduling switching cost (locked vault → cracked open)
const BLOG05 = () => (
  <Frame id="b05">
    <circle cx="180" cy="110" r="80" fill="url(#b05-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {/* legacy schedulers-style block */}
    <rect x="80" y="50" width="80" height="140" rx="4" fill="#0F1628" stroke="rgba(30,78,140,0.6)" strokeWidth="1" />
    <text x="120" y="68" textAnchor="middle" fontSize="8" fill="rgba(143,179,222,0.6)" fontFamily="var(--f-mono)" letterSpacing="1.4">legacy schedulers · LEGACY</text>
    {[80, 96, 112, 128, 144, 160].map((y, i) => (
      <rect key={i} x="90" y={y} width={50 - i * 6} height="3" rx="1" fill="rgba(143,179,222,0.45)" />
    ))}
    {/* crack lightning */}
    <path d="M160 60 L185 95 L170 110 L200 145 L180 175" fill="none" stroke="#F26B1F" strokeWidth="2"
      strokeDasharray="240" strokeDashoffset="240"
      style={{ animation: "drawLine 1.6s ease-in-out 0.4s forwards" }} />
    {/* new agent block */}
    <g style={{ animation: "fadeInSoft 1s ease 1.5s both" }}>
      <rect x="210" y="50" width="80" height="140" rx="4" fill="rgba(242,107,31,0.08)" stroke="#F26B1F" strokeWidth="1.2" />
      <text x="250" y="68" textAnchor="middle" fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.4" fontWeight="700">AGENT-NATIVE</text>
      {[80, 96, 112, 128, 144, 160].map((y, i) => (
        <rect key={i} x="220" y={y} width={50 - i * 5} height="3" rx="1" fill="#F26B1F" opacity={0.85 - i * 0.1} />
      ))}
      <circle cx="290" cy="180" r="3" fill="#F26B1F" style={{ animation: "pulseDot 1.4s ease-in-out infinite" }} />
    </g>
  </Frame>
);

// blog-06 — Seven RFIs that eat float (RFI tickets cascade)
const BLOG06 = () => (
  <Frame id="b06">
    <circle cx="180" cy="110" r="80" fill="url(#b06-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {[0, 1, 2, 3, 4, 5, 6].map((i) => {
      const x = 60 + i * 36;
      const y = 60 + (i % 2) * 18;
      const danger = i === 3 || i === 6;
      return (
        <g key={i} style={{ animation: `rise .7s ease ${i * 0.1}s both` }}>
          <rect x={x} y={y} width="40" height="28" rx="3" fill="#0F1628" stroke={danger ? "#F26B1F" : "rgba(30,78,140,0.6)"} strokeWidth="0.8" />
          <circle cx={x + 7} cy={y + 8} r="2" fill={danger ? "#F26B1F" : "#5790C9"} />
          <rect x={x + 14} y={y + 7} width="20" height="2" rx="1" fill="rgba(143,179,222,0.5)" />
          <rect x={x + 5} y={y + 14} width="32" height="2" rx="1" fill="rgba(143,179,222,0.4)" />
          <rect x={x + 5} y={y + 19} width="22" height="2" rx="1" fill="rgba(143,179,222,0.3)" />
          {danger && <text x={x + 35} y={y - 2} fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)">!</text>}
        </g>
      );
    })}
    {/* float bar shrinking */}
    <line x1="20" y1="170" x2="340" y2="170" stroke="rgba(143,179,222,0.3)" strokeWidth="1" strokeDasharray="3 3" />
    <rect x="30" y="180" width="200" height="6" rx="3" fill="#F26B1F" opacity="0.8" />
    <text x="240" y="186" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.4" fontWeight="700">FLOAT EATEN</text>
  </Frame>
);

// blog-07 — 4D schedule maintenance cost (gantt + meter)
const BLOG07 = () => (
  <Frame id="b07">
    <circle cx="180" cy="110" r="80" fill="url(#b07-steelglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {/* gantt rows */}
    {[
      { y: 50, x: 40, w: 220 }, { y: 70, x: 70, w: 180 }, { y: 90, x: 110, w: 150 },
      { y: 110, x: 140, w: 130 }, { y: 130, x: 80, w: 200 },
    ].map((b, i) => (
      <g key={i}>
        <rect x="20" y={b.y - 2} width="320" height="14" fill="rgba(30,78,140,0.08)" />
        <rect x={b.x} y={b.y} width={b.w} height="10" rx="2" fill={i === 4 ? "#F26B1F" : "#5790C9"} opacity={0.7 + i * 0.05}
          style={{ animation: `rise .6s ease ${i * 0.1}s both` }} />
      </g>
    ))}
    {/* cost meter */}
    <g style={{ animation: "fadeInSoft .8s ease .5s both" }}>
      <rect x="40" y="160" width="280" height="22" rx="4" fill="#0F1628" stroke="rgba(30,78,140,0.5)" strokeWidth="0.6" />
      <rect x="40" y="160" width="56" height="22" rx="4" fill="#F26B1F" opacity="0.85" />
      <text x="50" y="176" fontSize="9" fill="#fff" fontFamily="var(--f-mono)" letterSpacing="1.4" fontWeight="700">−80%</text>
      <text x="320" y="176" fontSize="8" fill="rgba(143,179,222,0.7)" fontFamily="var(--f-mono)" textAnchor="end" letterSpacing="1.4">MAINTENANCE COST</text>
    </g>
  </Frame>
);

// blog-08 — Reading FIDIC Red 20.1 (timeline 28-day countdown)
const BLOG08 = () => (
  <Frame id="b08">
    <circle cx="180" cy="110" r="80" fill="url(#b08-emberglow)" style={{ animation: "glowPulse 4s ease-in-out infinite" }} />
    {/* horizontal timeline */}
    <line x1="40" y1="110" x2="320" y2="110" stroke="rgba(143,179,222,0.3)" strokeWidth="1" />
    {/* day markers */}
    {[0, 7, 14, 21, 28].map((d, i) => {
      const x = 40 + (i / 4) * 280;
      const last = i === 4;
      return (
        <g key={i}>
          <line x1={x} y1="100" x2={x} y2="120" stroke={last ? "#F26B1F" : "rgba(143,179,222,0.5)"} strokeWidth={last ? "1.4" : "0.8"} />
          <text x={x} y="138" fontSize="9" fill={last ? "#F26B1F" : "rgba(143,179,222,0.55)"} fontFamily="var(--f-mono)" textAnchor="middle" letterSpacing="1.2" fontWeight={last ? 700 : 400}>D+{d}</text>
        </g>
      );
    })}
    {/* moving notice card */}
    <g style={{ animation: "fadeInSoft 1s ease 0.3s both" }}>
      <rect x="60" y="50" width="80" height="46" rx="4" fill="#0F1628" stroke="#F26B1F" strokeWidth="1" />
      <text x="100" y="66" fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)" textAnchor="middle" letterSpacing="1.4" fontWeight="700">CL 20.1</text>
      <rect x="68" y="74" width="64" height="3" rx="1" fill="rgba(143,179,222,0.5)" />
      <rect x="68" y="80" width="50" height="3" rx="1" fill="rgba(143,179,222,0.35)" />
      <rect x="68" y="86" width="40" height="3" rx="1" fill="rgba(143,179,222,0.25)" />
    </g>
    {/* warning at deadline */}
    <circle cx="320" cy="110" r="6" fill="#F26B1F" style={{ animation: "pulseDot 1.4s ease-in-out infinite" }} />
    <text x="320" y="80" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" textAnchor="middle" letterSpacing="1.6" fontWeight="700">TIME-BAR</text>
    <text x="180" y="180" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">28-DAY NOTICE WINDOW</text>
  </Frame>
);

// blog-09 — Commissioning defect rate (4× reduction arc)
const BLOG09 = () => (
  <Frame id="b09">
    <circle cx="180" cy="110" r="90" fill="url(#b09-emberglow)" style={{ animation: "glowPulse 4.5s ease-in-out infinite" }} />
    {/* big number with reduction arrow */}
    <text x="100" y="124" fontSize="48" fill="rgba(143,179,222,0.55)" fontFamily="var(--f-display)" fontWeight="600" textDecoration="line-through">100</text>
    <line x1="68" y1="108" x2="148" y2="108" stroke="rgba(143,179,222,0.55)" strokeWidth="2" />
    <path d="M165 110 H210" stroke="#F26B1F" strokeWidth="1.5" />
    <path d="M205 105 L213 110 L205 115" fill="none" stroke="#F26B1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <text x="240" y="124" fontSize="48" fill="#F26B1F" fontFamily="var(--f-display)" fontWeight="600"
      style={{ animation: "fadeInSoft 1s ease 0.5s both" }}>25</text>
    <text x="280" y="106" fontSize="11" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.2" fontWeight="700">−4×</text>
    {/* dotted arc connecting */}
    <path d="M120 70 Q180 30 250 70" fill="none" stroke="rgba(242,107,31,0.4)" strokeWidth="1" strokeDasharray="3 4" />
    <text x="180" y="180" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">DEFECTS · COMMISSIONING</text>
  </Frame>
);

// blog-10 — Platform + Agents pattern (orbiting agents around platform core)
const BLOG10 = () => (
  <Frame id="b10">
    <circle cx="180" cy="110" r="100" fill="url(#b10-steelglow)" style={{ animation: "glowPulse 5s ease-in-out infinite" }} />
    {/* central platform slab */}
    <rect x="130" y="86" width="100" height="48" rx="6" fill="#0F1628" stroke="#F26B1F" strokeWidth="1.4" />
    <text x="180" y="106" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.6" fontWeight="700">PLATFORM</text>
    <text x="180" y="124" textAnchor="middle" fontSize="7" fill="rgba(143,179,222,0.55)" fontFamily="var(--f-mono)" letterSpacing="1.4">8 PRODUCTS</text>
    {/* orbiting agent dots on dashed orbit */}
    <ellipse cx="180" cy="110" rx="115" ry="58" fill="none" stroke="rgba(143,179,222,0.3)" strokeWidth="0.7" strokeDasharray="3 3" />
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const a = (i * 60) * Math.PI / 180;
      const x = 180 + Math.cos(a) * 115;
      const y = 110 + Math.sin(a) * 58;
      return (
        <g key={i} style={{ transformOrigin: "180px 110px", animation: `spin ${24 + i * 2}s linear infinite` }}>
          <circle cx={x} cy={y} r="5" fill="#5790C9" style={{ animation: `pulseDot ${1.4 + i * 0.18}s ease-in-out infinite ${i * 0.2}s` }} />
        </g>
      );
    })}
    <text x="180" y="200" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">PLATFORM × AGENTS</text>
  </Frame>
);

/* ============== Registry & exported component ============== */
const REGISTRY = {
  "wp-01": WP01, "wp-02": WP02, "wp-03": WP03, "wp-04": WP04,
  "wp-05": WP05, "wp-06": WP06, "wp-07": WP07, "wp-08": WP08,
  "blog-01": BLOG01, "blog-02": BLOG02, "blog-03": BLOG03, "blog-04": BLOG04,
  "blog-05": BLOG05, "blog-06": BLOG06, "blog-07": BLOG07, "blog-08": BLOG08,
  "blog-09": BLOG09, "blog-10": BLOG10,
};

export default function ResourceArtwork({ id }) {
  const Cmp = REGISTRY[id] || WP01;
  return <Cmp />;
}
